import { UniButton } from '@/design-system/components/buttons';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { ModalHeader } from '@/design-system/components/modal';
import { TextBody, TextCaption, H6 } from '@/design-system/components/typography';
import { ScrollView, SxProp, View } from 'dripsy';
import { isEmpty, isString } from 'lodash';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutContext } from 'contexts/LayoutContext';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { FixedLimitStackParamList, FixedLimitStackRoutes } from '@/features/account-management/screens/fixed-limit/type';
import { AlertType, UniAlert } from '@/design-system/components/alerts';
import { errorParser } from '@/features/bill-payment/screens/otp/type';
import { formatAmountByDecimal } from '@/features/account-management/utils';

export interface FixedLimitProps {
  onClose: () => void;
  onLayout: (event: any) => void;
  data: any;
  func?: (limit: number, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
  onReturnData?: (data: any) => void;
  refresh?: () => void;
  record?: any;
}

export const FixedLimitWebOneScreen: React.FC<FixedLimitProps> = (props) => {
  const { onClose, onLayout, data, func, onReturnData, refresh, record } = props;
  const { t } = useTranslation('account-management');
  const layout = useContext(LayoutContext);
  const { body, textMb, textTitle, textAtmPin, btnView, btn } = sxStyles;
  const [loading, setLoading] = useState(false);

  const navigation = useNavigation<NativeStackNavigationProp<FixedLimitStackParamList>>();
  const [alertMessage, setAlertMessage] = useState('');

  return (
    <View
      sx={{
        height: 'auto',
        width: layout.dimensions.width,
        maxWidth: Math.min(500, layout.dimensions.width),
        maxHeight: layout.dimensions.height,
        backgroundColor: '$backgroundPrimary'
      }}
      onLayout={(event) => {
        onLayout(event);
      }}>
      <ModalHeader
        title={t('card-account-management::fixed-limit.confirmation-title--fixed-limit-confirmation')}
        closeModal={() => navigation.getParent()?.goBack()}
      />
      <ScrollView sx={{ flex: 1, backgroundColor: 'transparent' }} nestedScrollEnabled>
        <View sx={body}>
          <TextBody semiBold>{t('card-account-management::fixed-limit.confirmation-text--are-you-sure-to-fixed-limit')}</TextBody>
          <View>
            <TextCaption sx={textTitle} semiBold>
              {t('card-account-management::fixed-limit.form-text--limit')}
            </TextCaption>
            <H6 sx={textAtmPin}>
              {formatAmountByDecimal({ value: data.limit, decimals: 2 })} {record?.currency}
            </H6>
          </View>
          {!isEmpty(alertMessage) && <UniAlert type={AlertType.Error} message={alertMessage} sx={{ mt: 5 }} />}
          <View sx={btnView}>
            <UniButton
              sx={btn}
              disabled={loading}
              variant={ButtonVariant.outline1}
              label={t('card-account-management::set-card-limit.button--back')}
              onPress={() => {
                navigation?.goBack();
              }}
            />
            <UniButton
              sx={btn}
              loading={loading}
              labelSx={{ color: '$balck' }}
              label={t('card-account-management::general.button--place-instruction')}
              onPress={() => {
                if (func) {
                  setLoading(true);
                  func(
                    data.limit,
                    (returnData) => {
                      setLoading(false);
                      onReturnData && onReturnData(returnData.data);
                      navigation.navigate(FixedLimitStackRoutes.Placed, {
                        record,
                        onLayout: onLayout,
                        data,
                        returnData: returnData.data,
                        refresh
                      });
                    },
                    (error) => {
                      setLoading(false);
                      const data: any = error.response?.data;
                      const errorMessage = data && isString(data.message) ? errorParser(data.message, t) : errorParser(error.message, t);
                      setAlertMessage(errorMessage);
                    }
                  );
                }
              }}
            />
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  body: {
    minHeight: 200,
    minWidth: 500,
    px: 28,
    py: 28,
    gap: '$5'
  },
  textMb: {
    marginBottom: 20
  },
  textTitle: {
    marginBottom: 4,
    color: '$textSecondary'
  },
  textAtmPin: {
    fontWeight: 'bolder',
    color: '$textThemeSecondary'
  },
  btnView: {
    flex: 1,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '$3',
    marginTop: 20
  },
  btn: {
    width: 216,
    height: 44
  }
};
